import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { ChevronLeft, ChevronRight, Calendar, Wrench, Clock, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_COLORS = {
  scheduled: { bg: "bg-[#3B82F6]", dot: "bg-[#3B82F6]", text: "text-[#3B82F6]", label: "Scheduled" },
  completed: { bg: "bg-[#10B981]", dot: "bg-[#10B981]", text: "text-[#10B981]", label: "Completed" },
  overdue: { bg: "bg-[#DC2626]", dot: "bg-[#DC2626]", text: "text-[#DC2626]", label: "Overdue" },
  in_progress: { bg: "bg-[#F59E0B]", dot: "bg-[#F59E0B]", text: "text-[#F59E0B]", label: "In Progress" },
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function formatDate(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export default function MaintenanceCalendarPage({ onLogout, userRole, isFeatureEnabled }) {
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayRecords, setDayRecords] = useState([]);
  const [showDayModal, setShowDayModal] = useState(false);

  const fetchRecords = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/maintenance?limit=1000`, { headers: getAuthHeaders() });
      setRecords(resp.data);
    } catch {
      toast.error("Failed to load maintenance records");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const goToPrevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  };

  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  };

  const goToToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };

  // Group records by scheduled_date
  const recordsByDate = {};
  records.forEach((r) => {
    const d = r.scheduled_date;
    if (!d) return;
    if (!recordsByDate[d]) recordsByDate[d] = [];
    recordsByDate[d].push(r);
  });

  // Build calendar grid
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
  const prevMonthDays = getDaysInMonth(currentYear, currentMonth === 0 ? 11 : currentMonth - 1);

  const calendarDays = [];
  // Prev month fill
  for (let i = firstDay - 1; i >= 0; i--) {
    calendarDays.push({ day: prevMonthDays - i, isCurrentMonth: false, date: null });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    const date = formatDate(currentYear, currentMonth, d);
    calendarDays.push({ day: d, isCurrentMonth: true, date, records: recordsByDate[date] || [] });
  }
  // Next month fill
  const remaining = 42 - calendarDays.length;
  for (let i = 1; i <= remaining; i++) {
    calendarDays.push({ day: i, isCurrentMonth: false, date: null });
  }

  const todayStr = formatDate(today.getFullYear(), today.getMonth(), today.getDate());

  const handleDayClick = (dayData) => {
    if (!dayData.isCurrentMonth || dayData.records.length === 0) return;
    setSelectedDay(dayData.date);
    setDayRecords(dayData.records);
    setShowDayModal(true);
  };

  // Stats for current month view
  const monthRecords = records.filter((r) => r.scheduled_date && r.scheduled_date.startsWith(`${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`));
  const monthStats = {
    total: monthRecords.length,
    scheduled: monthRecords.filter((r) => r.status === "scheduled").length,
    completed: monthRecords.filter((r) => r.status === "completed").length,
    overdue: monthRecords.filter((r) => r.status === "overdue").length,
  };

  if (loading) {
    return (
      <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
        <div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading calendar...</div></div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="maintenance-calendar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight" data-testid="calendar-title">Maintenance Calendar</h1>
            <p className="text-sm text-[#64748B] mt-1">Visual overview of scheduled and completed maintenance</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday} className="rounded-none text-xs tracking-wide" data-testid="calendar-today-btn">TODAY</Button>
          </div>
        </div>

        {/* Month Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="calendar-stats">
          <StatCard icon={<Calendar size={18} />} label="Total" value={monthStats.total} color="#0F172A" />
          <StatCard icon={<Clock size={18} />} label="Scheduled" value={monthStats.scheduled} color="#3B82F6" />
          <StatCard icon={<CheckCircle2 size={18} />} label="Completed" value={monthStats.completed} color="#10B981" />
          <StatCard icon={<AlertTriangle size={18} />} label="Overdue" value={monthStats.overdue} color="#DC2626" />
        </div>

        {/* Calendar */}
        <div className="bg-white border border-[#E2E8F0]">
          {/* Month Navigation */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E8F0]">
            <button onClick={goToPrevMonth} className="p-2 hover:bg-[#F1F5F9] transition-colors" data-testid="calendar-prev-month">
              <ChevronLeft size={20} className="text-[#0F172A]" />
            </button>
            <h2 className="text-lg font-bold text-[#0F172A] tracking-wide" data-testid="calendar-month-label">
              {MONTHS[currentMonth]} {currentYear}
            </h2>
            <button onClick={goToNextMonth} className="p-2 hover:bg-[#F1F5F9] transition-colors" data-testid="calendar-next-month">
              <ChevronRight size={20} className="text-[#0F172A]" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b border-[#E2E8F0]">
            {DAYS.map((d) => (
              <div key={d} className="py-3 text-center text-xs font-bold text-[#64748B] tracking-wider uppercase">{d}</div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {calendarDays.map((dayData, i) => {
              const isToday = dayData.date === todayStr;
              const hasRecords = dayData.records && dayData.records.length > 0;
              const hasOverdue = hasRecords && dayData.records.some((r) => r.status === "overdue");

              return (
                <div
                  key={i}
                  className={`min-h-[90px] md:min-h-[110px] border-b border-r border-[#F1F5F9] p-1.5 transition-colors ${
                    dayData.isCurrentMonth ? "bg-white" : "bg-[#FAFAFA]"
                  } ${hasRecords ? "cursor-pointer hover:bg-[#F8FAFC]" : ""} ${isToday ? "ring-2 ring-inset ring-[#2563EB]" : ""}`}
                  onClick={() => handleDayClick(dayData)}
                  data-testid={dayData.date ? `calendar-day-${dayData.date}` : undefined}
                >
                  <div className={`text-sm font-medium mb-1 ${
                    !dayData.isCurrentMonth ? "text-[#CBD5E1]" : isToday ? "text-[#2563EB] font-bold" : hasOverdue ? "text-[#DC2626]" : "text-[#0F172A]"
                  }`}>
                    {dayData.day}
                  </div>
                  {hasRecords && (
                    <div className="space-y-0.5">
                      {dayData.records.slice(0, 3).map((r) => {
                        const sc = STATUS_COLORS[r.status] || STATUS_COLORS.scheduled;
                        return (
                          <div key={r.id} className={`${sc.bg} text-white text-[10px] px-1.5 py-0.5 truncate leading-tight`} title={r.title}>
                            {r.title}
                          </div>
                        );
                      })}
                      {dayData.records.length > 3 && (
                        <div className="text-[10px] text-[#64748B] pl-1">+{dayData.records.length - 3} more</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 px-6 py-3 border-t border-[#E2E8F0] bg-[#FAFAFA]" data-testid="calendar-legend">
            {Object.entries(STATUS_COLORS).map(([key, val]) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 ${val.dot}`}></span>
                <span className="text-xs text-[#64748B]">{val.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Day Detail Modal */}
      <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A] font-bold tracking-tight" data-testid="day-modal-title">
              <Calendar size={18} className="inline mr-2" />
              {selectedDay && new Date(selectedDay + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto" data-testid="day-modal-records">
            {dayRecords.map((r) => {
              const sc = STATUS_COLORS[r.status] || STATUS_COLORS.scheduled;
              return (
                <div key={r.id} className="border border-[#E2E8F0] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-sm text-[#0F172A]">{r.title}</h3>
                      <p className="text-xs text-[#64748B] mt-1">{r.description}</p>
                    </div>
                    <span className={`${sc.bg} text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider whitespace-nowrap`}>
                      {sc.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3 text-xs text-[#64748B]">
                    <span className="flex items-center gap-1"><Wrench size={12} /> {r.machine_number || "—"}</span>
                    {r.recurrence_days && <span className="flex items-center gap-1"><Clock size={12} /> Every {r.recurrence_days}d</span>}
                    {r.completed_date && <span className="flex items-center gap-1"><CheckCircle2 size={12} /> Done: {r.completed_date}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-white border border-[#E2E8F0] px-4 py-3 flex items-center gap-3" data-testid={`calendar-stat-${label.toLowerCase()}`}>
      <div style={{ color }}>{icon}</div>
      <div>
        <div className="text-xl font-bold text-[#0F172A]">{value}</div>
        <div className="text-xs text-[#64748B] uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}
